import { useEffect, useRef } from 'react';
import { useToast } from './useToast';
import type { ToastItem } from './ToastContext';

/** Compatibility for controlled notices: all rendering stays in ToastProvider. */
export function ToastNotice({ open, message, type = 'info', duration = 4000, onClose }: {
  open: boolean;
  message: string | null | undefined;
  type?: ToastItem['type'];
  duration?: number;
  onClose?: () => void;
}) {
  const { push } = useToast();
  const closeOwner = useRef(onClose); closeOwner.current = onClose;
  useEffect(() => {
    if (!open || !message) return;
    let current = true;
    const close = () => { if (current) { current = false; closeOwner.current?.(); } };
    const dismiss = push(message, type, duration, { onDismiss: close });
    const timer = window.setTimeout(close, duration);
    // Match controlled Snackbar dismissal without treating its opening click
    // or an Escape owned by a nested confirmation as a dismissal.
    let activated = false;
    const activate = window.setTimeout(() => { activated = true; }, 0);
    const clickAway = (event: MouseEvent) => {
      if (activated && event.target instanceof Element && !event.target.closest('.app-toast')) close();
    };
    const escape = (event: KeyboardEvent) => { if (event.key === 'Escape' && !event.defaultPrevented) close(); };
    document.addEventListener('click', clickAway);
    document.addEventListener('keydown', escape);
    return () => {
      current = false; window.clearTimeout(timer); window.clearTimeout(activate);
      document.removeEventListener('click', clickAway); document.removeEventListener('keydown', escape);
      if (typeof dismiss === 'function') dismiss();
    };
  }, [open, message, type, duration, push]);
  return null;
}
